/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";

// @material-ui/core
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import withStyles from "@material-ui/core/styles/withStyles";
import MapLeft from "components/MapLeft/MapLeft.jsx";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Box from "@material-ui/core/Box";
import Modal from "@material-ui/core/Modal";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";

// core component
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomSelect from "components/CustomSelect/CustomSelect.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import Table from "components/Table/Table.jsx";
import StatusTypeEditor from "components/StatusTypeEditor/StatusTypeEditor.jsx";

import {
  updateUserDevice,
  getDeviceDocs,
  uploadDeviceDocs,
  deleteDocument,
  startReprocessDeviceStatuses,
  getReprocessDeviceStatus
} from "../../services/dashboardDataProvider";
import { dayjs } from "utils/time.jsx";
import {
  getDeviceDetailsPalette,
  isDarkUiTheme,
  devicePanelGradient,
  deviceSurfaceColor
} from "theme/deviceDetailsTheme.js";
import { reportDarkPalette } from "assets/jss/material-dashboard-react.jsx";
import { getAppShellUi } from "theme/appShellTheme.js";

// import { MEDIA_ROOT } from "config.js";

const REPROCESS_TARGET_TYPE_OPTIONS = [
  { value: "device", label: "Device" },
  { value: "user", label: "User" },
  { value: "meter", label: "Meter" },
  { value: "alarm", label: "Alarm" },
  { value: "report", label: "Report" }
];

const styles = theme => {
  const isDark = isDarkUiTheme(theme);
  const ui = getAppShellUi(isDark);
  const DEVICE_DETAILS_THEME = getDeviceDetailsPalette(isDark);
  const panelGradient = devicePanelGradient(isDark);
  const surface = deviceSurfaceColor(isDark);

  return {
  root: {
    width: "100%"
  },
  statusSummaryControls: {
    margin: "0"
  },
  topBar: {
    marginBottom: "18px",
    borderRadius: "18px",
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    background: `linear-gradient(135deg, ${DEVICE_DETAILS_THEME.panelStart} 0%, ${DEVICE_DETAILS_THEME.panelEnd} 100%)`,
    boxShadow: "0 14px 28px rgba(29, 78, 216, 0.12)",
    padding: "14px 18px"
  },
  topBarContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap"
  },
  topBarTitleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  topBarTitle: {
    color: DEVICE_DETAILS_THEME.textMain,
    fontWeight: 700,
    letterSpacing: "0.01em"
  },
  topBarSubtitle: {
    color: DEVICE_DETAILS_THEME.textMuted
  },
  topBarActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap"
  },
  adminActionButton: {
    boxShadow: "0 10px 20px rgba(37, 99, 235, 0.12)"
  },
  detailsContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  metersContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  detailsTable: {
    paddingBottom: "10px"
  },
  deviceInputActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "18px"
  },
  reprocessSummaryCard: {
    marginTop: "16px",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    background: panelGradient,
    padding: "14px 16px"
  },
  reprocessFieldsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px",
    marginTop: "8px"
  },
  reprocessField: {
    flex: "1 1 220px",
    minWidth: "220px"
  },
  reprocessToggleRow: {
    marginTop: "6px"
  },
  reprocessError: {
    color: "#b91c1c",
    marginTop: "12px"
  },
  reprocessResult: {
    marginTop: "12px",
    color: DEVICE_DETAILS_THEME.textMain
  },
  reprocessConfirmationBox: {
    marginTop: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid rgba(37, 99, 235, 0.18)",
    background: "rgba(37, 99, 235, 0.06)"
  },
  reprocessConfirmationLine: {
    color: "#1e293b",
    marginTop: "6px"
  },
  reprocessStatsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "10px"
  },
  deviceTypeSelectorContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  },
  rawTelemetryDataContainer: {
    display: "flex",
    flexDirection: "column",
    overflowX: "scroll"
  },
  jsonContainer: {
    display: "flex",
    flexDirection: "column"
  },
  jsonKeyValuePair: {
    display: "flex",
    flexDirection: "row"
  },
  jsonKey: {
    fontSize: "larger",
    color: "#0057ff",
    display: "flex",
    flexDirection: "row"
  },
  jsonKeyValue: {
    display: "flex",
    flexDirection: "row",
    color: "#ff00a0",
    paddingLeft: "5px"
  },
  alarmActions: {
    display: "flex",
    justifyContent: "flex-end"
  },
  alarmModalBox: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(760px, 92vw)",
    maxHeight: "82vh",
    overflowY: "auto",
    opacity: "100%",
    zIndex: 2000,
    backgroundColor: surface,
    borderRadius: "18px",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    "-webkit-box-shadow": "0 24px 48px rgba(15, 23, 42, 0.18)",
    "-moz-box-shadow": "0 24px 48px rgba(15, 23, 42, 0.18)",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
    "-webkit-background-clip": "padding-box",
    "-moz-background-clip": "padding-box",
    backgroundClip: "padding-box"
  },
  alarmDetailsContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: "10px 0"
  },
  alarmSelectionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px"
  },
  deviceDetailsSectionContainer: {
    marginTop: "10px",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    borderRadius: "18px",
    background: panelGradient,
    boxShadow: isDark
      ? "none"
      : "0 10px 26px rgba(15, 23, 42, 0.06)",
    overflow: "hidden"
  },
  deviceTypeContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "14px 18px",
    background: isDark
      ? `linear-gradient(90deg, ${ui.accentSoft} 0%, rgba(128, 163, 255, 0.04) 100%)`
      : "linear-gradient(90deg, rgba(25,118,210,0.10) 0%, rgba(25,118,210,0.04) 100%)"
  },
  materialSectionCard: {
    marginTop: "10px",
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    borderRadius: "12px",
    background: surface,
    boxShadow: isDark ? "none" : "0 6px 18px rgba(30, 58, 138, 0.08)",
    overflow: "hidden"
  },
  materialSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    background: isDark
      ? `linear-gradient(90deg, ${DEVICE_DETAILS_THEME.primarySoft} 0%, rgba(128, 163, 255, 0.06) 100%)`
      : `linear-gradient(90deg, ${DEVICE_DETAILS_THEME.primarySoft} 0%, rgba(219, 234, 254, 0.35) 100%)`
  },
  materialSectionTitle: {
    color: DEVICE_DETAILS_THEME.primaryDark,
    fontWeight: 600
  },
  materialSectionBody: {
    padding: "10px 16px 16px"
  },
  sectionDescription: {
    color: DEVICE_DETAILS_THEME.textMuted,
    marginBottom: "14px"
  },
  infoRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    padding: "10px 0"
  },
  infoLabel: {
    minWidth: "170px",
    color: DEVICE_DETAILS_THEME.textMuted,
    textTransform: "uppercase",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.4px"
  },
  infoValue: {
    flex: 1,
    color: DEVICE_DETAILS_THEME.textMain,
    fontSize: "14px",
    wordBreak: "break-word"
  },
  editFieldCard: {
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    borderRadius: "14px",
    padding: "12px 14px",
    background: panelGradient,
    height: "100%"
  },
  editLabel: {
    display: "block",
    marginBottom: "6px",
    color: DEVICE_DETAILS_THEME.textMuted,
    fontSize: "12px",
    fontWeight: 600
  },
  settingsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "12px"
  },
  settingsToggleGrid: {
    marginTop: "8px"
  },
  meterChip: {
    margin: "4px"
  },
  inputSectionCard: {
    marginTop: "18px"
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap"
  },
  documentTableLink: {
    cursor: "pointer",
    color: DEVICE_DETAILS_THEME.primary,
    textDecoration: "none",
    fontWeight: 600
  },
  inlineFormCard: {
    border: "1px dashed rgba(37, 99, 235, 0.28)",
    borderRadius: "14px",
    padding: "14px",
    margin: "8px 0 16px",
    background: "rgba(239, 246, 255, 0.8)"
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "18px"
  },
  modalTitleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  modalPreview: {
    marginTop: 16,
    minHeight: 300,
    textAlign: "center",
    borderRadius: "14px",
    padding: "16px",
    background: isDark ? reportDarkPalette.bg : "#f8fafc",
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`
  },
  modalPreviewImage: {
    maxWidth: "100%",
    maxHeight: 400,
    borderRadius: "12px"
  },
  modalNavButton: {
    border: "1px solid rgba(148, 163, 184, 0.24)",
    borderRadius: "12px"
  },
  stateFieldCard: {
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    borderRadius: "14px",
    padding: "12px 14px",
    background: surface,
    height: "100%"
  },
  helperCard: {
    border: `1px dashed ${DEVICE_DETAILS_THEME.border}`,
    borderRadius: "14px",
    padding: "12px 14px",
    marginBottom: "14px",
    background: isDark
      ? "rgba(128, 163, 255, 0.08)"
      : "rgba(239, 246, 255, 0.95)"
  },
  helperTitle: {
    color: DEVICE_DETAILS_THEME.textMain,
    fontWeight: 700,
    marginBottom: "8px"
  },
  helperSubtitle: {
    color: DEVICE_DETAILS_THEME.textMuted,
    marginBottom: "10px"
  },
  helperGroupTitle: {
    color: DEVICE_DETAILS_THEME.textMain,
    fontWeight: 600,
    marginBottom: "6px"
  },
  helperChipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "10px"
  },
  helperChip: {
    fontFamily: "'Roboto Mono', 'Consolas', monospace",
    fontSize: "0.75rem"
  },
  editWorkspaceShell: {
    marginTop: "10px",
    borderRadius: "16px",
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    background: panelGradient,
    boxShadow: "0 14px 34px rgba(30, 58, 138, 0.12)",
    padding: "14px"
  },
  editWorkspaceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "10px"
  },
  editWorkspaceNav: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px"
  },
  editWorkspaceNavChip: {
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    color: DEVICE_DETAILS_THEME.primaryDark,
    background: surface
  },
  editWorkspaceAnchor: {
    marginTop: "6px"
  },
  globalEditActions: {
    borderRadius: "14px",
    border: `1px solid ${DEVICE_DETAILS_THEME.border}`,
    background: isDark ? reportDarkPalette.elevated : "rgba(255,255,255,0.96)",
    boxShadow: "0 8px 22px rgba(37, 99, 235, 0.14)",
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap"
  },
  globalEditActionsText: {
    color: DEVICE_DETAILS_THEME.textMuted
  },
  globalEditActionsButtons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
  },
  globalEditActionsDesktop: {
    position: "sticky",
    top: "8px",
    zIndex: 5,
    marginTop: "10px",
    marginBottom: "8px",
    "@media (max-width: 960px)": {
      display: "none"
    }
  },
  globalEditActionsMobile: {
    position: "fixed",
    left: "12px",
    right: "12px",
    bottom: "12px",
    zIndex: 1200,
    display: "none",
    "@media (max-width: 960px)": {
      display: "flex"
    }
  }
};
};

const LCD_STATUS_HELPER_KEYS = [
  "net_meter_status",
  "solar_status",
  "battery_charging_status",
  "load_status",
  "system_temperature"
];

const CHART_STATUS_HELPER_KEYS = [
  "solar_status",
  "load_status",
  "energy_generated_this_day",
  "energy_consumed_this_day",
  "battery_charging_status",
  "system_temperature",
  "system_humidity",
  "uptime",
  "net_meter_power_factor",
  "system_status",
  "energy_exported_this_month",
  "energy_exported_this_day",
  "energy_imported_this_month",
  "cost_savings_this_month",
  "energy_imported_this_day"
];

const SUMMARY_PROPERTY_HELPER_KEYS = [
  "monthly_bill_limit",
  "monthly_bill_amount",
  "actual_energy_bill_last_month",
  "pay_per_unit",
  "currency",
  "energy_consumption_limit_this_month",
  "energy_consumption_limit_this_day",
  "energy_generation_limit_this_month",
  "energy_generation_limit_this_day",
  "actual_import_readings",
  "actual_export_readings",
  "total_installation_cost",
  "site_name",
  "site_lat",
  "site_lon",
  "plant_solar_dc_kw",
  "plant_inverter_ac_kw",
  "plant_household_load_kw",
  "plant_battery_ah",
  "plant_battery_age_years",
  "plant_battery_type",
  "plant_tilt_deg",
  "plant_azimuth",
  "plant_performance_ratio",
  "tariff_import_inr_per_kwh",
  "tariff_export_inr_per_kwh",
  "tariff_fixed_inr_per_month",
  "tariff_notes",
  "co2_kg_per_kwh"
];

const SUMMARY_TEXT_KEYS = new Set([
  "currency",
  "site_name",
  "plant_battery_type",
  "plant_azimuth",
  "tariff_notes"
]);

const SUMMARY_PROPERTY_METADATA = {
  monthly_bill_limit: {
    label: "Monthly Bill Limit",
    group: "Summary",
    description: "Dashboard alert threshold for monthly billing.",
    suggestedDefault: "No default"
  },
  monthly_bill_amount: {
    label: "Monthly Bill Amount",
    group: "Summary",
    description: "Current monthly bill amount used for dashboard display.",
    suggestedDefault: "No default"
  },
  actual_energy_bill_last_month: {
    label: "Actual Energy Bill Last Month",
    group: "Summary",
    description: "Reference value for last month bill comparison.",
    suggestedDefault: "No default"
  },
  pay_per_unit: {
    label: "Pay Per Unit",
    group: "Summary",
    description: "Generic unit cost used by summary widgets.",
    suggestedDefault: "7.00",
    defaultValue: "7.00"
  },
  currency: {
    label: "Currency",
    group: "Summary",
    description: "Currency symbol/code for dashboard and report amounts.",
    suggestedDefault: "INR",
    defaultValue: "INR"
  },
  energy_consumption_limit_this_month: {
    label: "Energy Consumption Limit This Month",
    group: "Summary",
    description: "Monthly consumption threshold for notifications.",
    suggestedDefault: "No default"
  },
  energy_consumption_limit_this_day: {
    label: "Energy Consumption Limit This Day",
    group: "Summary",
    description: "Daily consumption threshold for notifications.",
    suggestedDefault: "No default"
  },
  energy_generation_limit_this_month: {
    label: "Energy Generation Limit This Month",
    group: "Summary",
    description: "Monthly generation target or alert threshold.",
    suggestedDefault: "No default"
  },
  energy_generation_limit_this_day: {
    label: "Energy Generation Limit This Day",
    group: "Summary",
    description: "Daily generation target or alert threshold.",
    suggestedDefault: "No default"
  },
  actual_import_readings: {
    label: "Actual Import Readings",
    group: "Summary",
    description: "Latest utility import reading baseline.",
    suggestedDefault: "No default"
  },
  actual_export_readings: {
    label: "Actual Export Readings",
    group: "Summary",
    description: "Latest utility export reading baseline.",
    suggestedDefault: "No default"
  },
  total_installation_cost: {
    label: "Total Installation Cost",
    group: "Summary",
    description: "Total CAPEX value used in ROI displays.",
    suggestedDefault: "No default"
  },
  site_name: {
    label: "Site Name",
    group: "Report Site",
    description: "Location name shown in report header.",
    suggestedDefault: "Device alias or name"
  },
  site_lat: {
    label: "Site Latitude",
    group: "Report Site",
    description: "Latitude used for solar model calculations.",
    suggestedDefault: "Use device latitude"
  },
  site_lon: {
    label: "Site Longitude",
    group: "Report Site",
    description: "Longitude used for solar model calculations.",
    suggestedDefault: "Use device longitude"
  },
  plant_solar_dc_kw: {
    label: "Plant Solar DC (kW)",
    group: "Report Plant",
    description: "Installed PV DC capacity.",
    suggestedDefault: "3.2",
    defaultValue: "3.2"
  },
  plant_inverter_ac_kw: {
    label: "Plant Inverter AC (kW)",
    group: "Report Plant",
    description: "Inverter AC limit used in report model.",
    suggestedDefault: "5.0",
    defaultValue: "5.0"
  },
  plant_household_load_kw: {
    label: "Plant Household Load (kW)",
    group: "Report Plant",
    description: "Representative max household load.",
    suggestedDefault: "5.0",
    defaultValue: "5.0"
  },
  plant_battery_ah: {
    label: "Plant Battery (Ah)",
    group: "Report Plant",
    description: "Battery bank capacity in Ah.",
    suggestedDefault: "4800",
    defaultValue: "4800"
  },
  plant_battery_age_years: {
    label: "Plant Battery Age (Years)",
    group: "Report Plant",
    description: "Battery age shown in report context.",
    suggestedDefault: "5",
    defaultValue: "5"
  },
  plant_battery_type: {
    label: "Plant Battery Type",
    group: "Report Plant",
    description: "Battery chemistry/type label for the report.",
    suggestedDefault: "Lead Acid",
    defaultValue: "Lead Acid"
  },
  plant_tilt_deg: {
    label: "Plant Tilt (Deg)",
    group: "Report Plant",
    description: "Panel tilt angle used in model assumptions.",
    suggestedDefault: "Use model default"
  },
  plant_azimuth: {
    label: "Plant Azimuth",
    group: "Report Plant",
    description: "Panel facing direction text.",
    suggestedDefault: "south",
    defaultValue: "south"
  },
  plant_performance_ratio: {
    label: "Plant Performance Ratio",
    group: "Report Plant",
    description: "Performance ratio used for expected generation.",
    suggestedDefault: "Use model default"
  },
  tariff_import_inr_per_kwh: {
    label: "Tariff Import (per kWh)",
    group: "Report Tariff",
    description: "Import energy rate used in daily bill estimate.",
    suggestedDefault: "8.40",
    defaultValue: "8.40"
  },
  tariff_export_inr_per_kwh: {
    label: "Tariff Export (per kWh)",
    group: "Report Tariff",
    description: "Export credit rate used in report.",
    suggestedDefault: "3.26",
    defaultValue: "3.26"
  },
  tariff_fixed_inr_per_month: {
    label: "Tariff Fixed Charge (Monthly)",
    group: "Report Tariff",
    description: "Monthly fixed utility charge.",
    suggestedDefault: "800",
    defaultValue: "800"
  },
  tariff_notes: {
    label: "Tariff Notes",
    group: "Report Tariff",
    description: "Narrative notes shown in report tariff section.",
    suggestedDefault:
      "RERC FY 2025-26 domestic 151-500 slab Rs7.00 + Rs1.00 surcharge + Rs0.40 duty. Export credit Rs3.26/kWh JVVNL net metering (Oct 2025). Fixed charge prorated from Rs800/month.",
    defaultValue:
      "RERC FY 2025-26 domestic 151-500 slab Rs7.00 + Rs1.00 surcharge + Rs0.40 duty. Export credit Rs3.26/kWh JVVNL net metering (Oct 2025). Fixed charge prorated from Rs800/month."
  },
  co2_kg_per_kwh: {
    label: "CO2 Kg per kWh",
    group: "Report Emissions",
    description: "Grid emission factor for CO2 calculations.",
    suggestedDefault: "0.727",
    defaultValue: "0.727"
  }
};

const getSummaryPropertyMeta = key => {
  if (SUMMARY_PROPERTY_METADATA[key]) {
    return SUMMARY_PROPERTY_METADATA[key];
  }
  const fallbackLabel = key
    .split("_")
    .join(" ")
    .replace(/\b\w/g, char => char.toUpperCase());
  return {
    label: fallbackLabel,
    group: "Summary",
    description: "",
    suggestedDefault: "No default"
  };
};

const REPORT_PROPERTY_KEYS = SUMMARY_PROPERTY_HELPER_KEYS.filter(
  key => (getSummaryPropertyMeta(key).group || "").indexOf("Report") === 0
);

const ALL_CONFIGURABLE_PROPERTY_KEYS = [...SUMMARY_PROPERTY_HELPER_KEYS];

let deviceTypeOptions = [
  { text: "Home", value: "Home" },
  { text: "Charge Controller", value: "Charge Controller" },
  { text: "DELTA-RPI Inverter", value: "DELTA-RPI Inverter" },
  { text: "WEATHER_STATION", value: "WEATHER STATION" },
  { text: "SOLAR HYBRID INVERTER", value: "SOLAR HYBRID INVERTER" },
  { text: "IOT_GATEWAY", value: "IOT_GATEWAY" },
  { text: "SOLAR PUMP", value: "SOLAR PUMP" },
  { text: "OTHER", value: "OTHER" }
];

class DeviceDetailsTable extends React.Component {
  constructor(props) {
    super(props);
    const defaultDay = dayjs().format("YYYY-MM-DD");
    const defaultStart = dayjs().startOf("day").format("YYYY-MM-DDTHH:mm");
    const defaultEnd = dayjs().format("YYYY-MM-DDTHH:mm");
    this.state = {
      editDeviceDetails: false,
      updatedData: {},
      detailsExpanded: true,
      metersExpanded: true,
      documentModalOpen: false,
      documentModalIndex: 0,
      documentsLoaded: false,
      schemaInputMode: "text", // "text" or "interactive"
      editingSchemaFields: null, // Copy of schema fields for interactive editing
      summaryKeysAdded: [],
      summaryKeysRemoved: [],
      summaryKeyToAdd: "",
      reprocessModalOpen: false,
      reprocessMode: "day",
      reprocessDay: defaultDay,
      reprocessStart: defaultStart,
      reprocessEnd: defaultEnd,
      reprocessStatusIntervalMinutes: 10,
      reprocessTargetTypes: REPROCESS_TARGET_TYPE_OPTIONS.map(option => option.value),
      reprocessKeepExisting: false,
      reprocessSubmitting: false,
      reprocessError: "",
      reprocessResult: null,
      reprocessConfirming: false,
      reprocessPreview: null,
      reprocessJobId: null,
      reprocessJobStatus: "idle",
      reprocessJobProgress: null,
      reprocessProgressPercent: 0,
      reprocessProgressMessage: ""
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchDeviceDocsIfNeeded();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.clearReprocessPollTimeout();
  }

  clearReprocessPollTimeout = () => {
    if (this.reprocessPollTimeout) {
      clearTimeout(this.reprocessPollTimeout);
      this.reprocessPollTimeout = null;
    }
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.deviceData &&
      this.props.deviceData !== prevProps.deviceData
    ) {
      this.setState(state => ({
        deviceData: {
          ...this.props.deviceData,
          documents:
            this.props.deviceData.documents ||
            (state.deviceData && state.deviceData.documents) ||
            []
        }
      }));
    }

    // Fetch docs if deviceData changes (e.g., when tab is activated)
    if (
      this.props.deviceData &&
      (!prevProps.deviceData ||
        this.props.deviceData.ip_address !== prevProps.deviceData.ip_address)
    ) {
      this.fetchDeviceDocsIfNeeded();
    }
  }

  fetchDeviceDocsIfNeeded = () => {
    const { deviceData, axios } = this.props;
    if (deviceData && deviceData.ip_address && !this.state.documentsLoaded) {
      getDeviceDocs(axios, deviceData.ip_address).then(docs => {
        // Merge docs into deviceData for rendering
        this.setState(state => ({
          deviceData: {
            ...(state.deviceData || deviceData),
            documents: docs
          },
          documentsLoaded: true
        }));
      });
    } else {
      this.setState({ deviceData: deviceData, documentsLoaded: true });
    }
  };

  onChange = name => e => {
    let updatedData = this.state.updatedData || {};
    updatedData[name] = e.target.value;
    this.setState({ updatedData: updatedData });
  };

  onSelectionChange = name => value => {
    let updatedData = this.state.updatedData || {};
    updatedData[name] = value;
    this.setState({ updatedData: updatedData });
  };

  onDataChange = (dataType, idx, fieldName) => e => {
    const { deviceData } = this.state;
    let dataList = deviceData[dataType];
    if (dataList && dataList[idx]) {
      let value = e.target.value;
      if (fieldName === "data_schema" || fieldName === "translation_schema") {
        if (this.isValidJson(value)) {
          value = JSON.parse(value);
          dataList[idx][fieldName] = value;
        }
      } else {
        dataList[idx][fieldName] = value;
      }
      this.setState({ [`updated${dataType}`]: dataList });
    }
  };

  onSelectionChangeStatus = (dataType, idx, fieldName) => value => {
    const { deviceData } = this.state;
    let dataList = deviceData[dataType];
    if (dataList && dataList[idx]) {
      dataList[idx][fieldName] = value;
      this.setState({ [`updated${dataType}`]: dataList });
    }
  };

  onMeterTypeSelectionChange = meterId => value => {
    let updatedData = this.state.updatedData || {};
    let meters = updatedData.meters || [];
    let meter = meters.find(mtr => mtr.id === meterId);
    if (meter) {
      meter.meter_type = value;
    } else {
      meter = {
        id: meterId,
        meter_type: value
      };
      meters.push(meter);
    }
    updatedData.meters = meters;
    this.setState({ updatedData: updatedData });
  };

  onStateInputChange = stateName => e => {
    let updatedData = this.state.updatedData || {};
    let properties = updatedData.properties || {};
    properties[stateName] = e.target.value;
    updatedData.properties = properties;
    this.setState({ updatedData: updatedData });
  };

  addReportDefaults = () => {
    this.setState(state => {
      const deviceData = state.deviceData || this.props.deviceData || {};
      const existingDeviceProperties = { ...(deviceData.properties || {}) };
      const updatedData = { ...(state.updatedData || {}) };
      const draftProperties = { ...(updatedData.properties || {}) };
      const summaryKeysRemovedSet = new Set(state.summaryKeysRemoved || []);

      REPORT_PROPERTY_KEYS.forEach(key => {
        const meta = getSummaryPropertyMeta(key);
        const hasDraftValue = draftProperties[key] != null && draftProperties[key] !== "";
        const hasExistingValue =
          existingDeviceProperties[key] != null && existingDeviceProperties[key] !== "";
        if (!hasDraftValue && !hasExistingValue && meta.defaultValue !== undefined) {
          draftProperties[key] = meta.defaultValue;
        }
        summaryKeysRemovedSet.delete(key);
      });

      const summaryKeysAdded = Array.from(
        new Set([...(state.summaryKeysAdded || []), ...REPORT_PROPERTY_KEYS])
      );

      return {
        updatedData: {
          ...updatedData,
          properties: draftProperties
        },
        summaryKeysAdded,
        summaryKeysRemoved: Array.from(summaryKeysRemovedSet)
      };
    });
  };

  addAllDefaults = () => {
    this.setState(state => {
      const deviceData = state.deviceData || this.props.deviceData || {};
      const existingDeviceProperties = { ...(deviceData.properties || {}) };
      const updatedData = { ...(state.updatedData || {}) };
      const draftProperties = { ...(updatedData.properties || {}) };
      const summaryKeysRemovedSet = new Set(state.summaryKeysRemoved || []);

      ALL_CONFIGURABLE_PROPERTY_KEYS.forEach(key => {
        const meta = getSummaryPropertyMeta(key);
        const hasDraftValue = draftProperties[key] != null && draftProperties[key] !== "";
        const hasExistingValue =
          existingDeviceProperties[key] != null && existingDeviceProperties[key] !== "";
        if (!hasDraftValue && !hasExistingValue && meta.defaultValue !== undefined) {
          draftProperties[key] = meta.defaultValue;
        }
        summaryKeysRemovedSet.delete(key);
      });

      const summaryKeysAdded = Array.from(
        new Set([...(state.summaryKeysAdded || []), ...ALL_CONFIGURABLE_PROPERTY_KEYS])
      );

      return {
        updatedData: {
          ...updatedData,
          properties: draftProperties
        },
        summaryKeysAdded,
        summaryKeysRemoved: Array.from(summaryKeysRemovedSet)
      };
    });
  };

  addSummaryKey = key => {
    if (!key) return;
    this.setState(state => ({
      summaryKeysAdded: [...new Set([...state.summaryKeysAdded, key])],
      summaryKeysRemoved: state.summaryKeysRemoved.filter(k => k !== key),
      summaryKeyToAdd: ""
    }));
  };

  removeSummaryKey = key => {
    this.setState(state => {
      const properties = { ...((state.updatedData && state.updatedData.properties) || {}) };
      delete properties[key];
      return {
        summaryKeysRemoved: [...state.summaryKeysRemoved, key],
        summaryKeysAdded: state.summaryKeysAdded.filter(k => k !== key),
        updatedData: { ...state.updatedData, properties }
      };
    });
  };

  onOtherDataCheckboxChange = key => e => {
    const checked = e.target.checked;
    const updatedData = this.state.updatedData || {};
    const existingOtherData = {
      ...((this.state.deviceData && this.state.deviceData.other_data) || {}),
      ...(updatedData.other_data || {})
    };

    updatedData.other_data = {
      ...existingOtherData,
      [key]: checked
    };

    this.setState({ updatedData: updatedData });
  };

  isValidJson(text) {
    if (typeof text !== "string") {
      return false;
    }
    try {
      JSON.parse(text);
      return true;
    } catch (error) {
      return false;
    }
  }

  updateProfile = deviceId => e => {
    updateUserDevice(this.props.axios, deviceId, this.state.updatedData).then(
      data => {
        this.resetUpdateState(data);
      }
    );
    e.preventDefault();
  };

  scrollToSection = sectionId => {
    const section = document.getElementById(sectionId);
    if (!section) return;
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  openReprocessModal = () => {
    this.setState({
      reprocessModalOpen: true,
      reprocessError: "",
      reprocessResult: null,
      reprocessConfirming: false,
      reprocessPreview: null,
      reprocessDay: this.state.reprocessDay || dayjs().format("YYYY-MM-DD"),
      reprocessStart:
        this.state.reprocessStart ||
        dayjs().startOf("day").format("YYYY-MM-DDTHH:mm"),
      reprocessEnd:
        this.state.reprocessEnd || dayjs().format("YYYY-MM-DDTHH:mm")
    });
  };

  closeReprocessModal = () => {
    if (this.state.reprocessSubmitting) return;
    this.clearReprocessPollTimeout();
    this.setState({
      reprocessModalOpen: false,
      reprocessError: "",
      reprocessConfirming: false,
      reprocessPreview: null,
      reprocessJobId: null,
      reprocessJobStatus: "idle",
      reprocessJobProgress: null,
      reprocessProgressPercent: 0,
      reprocessProgressMessage: ""
    });
  };

  toggleReprocessTargetType = targetType => {
    this.setState(state => {
      const targetTypes = new Set(state.reprocessTargetTypes || []);
      if (targetTypes.has(targetType)) {
        targetTypes.delete(targetType);
      } else {
        targetTypes.add(targetType);
      }

      return {
        reprocessTargetTypes: Array.from(targetTypes),
        reprocessError: "",
        reprocessConfirming: false,
        reprocessPreview: null,
        reprocessResult: null
      };
    });
  };

  getReprocessRequestDetails = () => {
    const selectedTargetTypes = this.state.reprocessTargetTypes || [];
    if (selectedTargetTypes.length === 0) {
      return { error: "Choose at least one status target to reprocess." };
    }

    const payload = {
      mode: this.state.reprocessMode,
      keep_existing_statuses: this.state.reprocessKeepExisting,
      status_interval_minutes: this.state.reprocessStatusIntervalMinutes,
      target_types: selectedTargetTypes
    };

    const intervalMinutes = parseInt(this.state.reprocessStatusIntervalMinutes, 10);
    if (Number.isNaN(intervalMinutes) || intervalMinutes < 2 || intervalMinutes > 60) {
      return { error: "Status interval must be between 2 and 60 minutes." };
    }

    if (this.state.reprocessMode === "range") {
      if (!this.state.reprocessStart || !this.state.reprocessEnd) {
        return {
          error: "Start and end time are required for range reprocessing."
        };
      }

      const start = dayjs(this.state.reprocessStart);
      const end = dayjs(this.state.reprocessEnd);

      if (!start.isValid() || !end.isValid()) {
        return { error: "Start and end time must be valid values." };
      }

      if (!end.isAfter(start)) {
        return { error: "End time must be later than start time." };
      }

      payload.start = start.toISOString();
      payload.end = end.toISOString();

      return {
        payload,
        preview: [
          `Mode: exact time range`,
          `Status interval: ${intervalMinutes} minute${intervalMinutes === 1 ? "" : "s"}`,
          `Targets: ${selectedTargetTypes.join(", ")}`,
          `Start: ${start.format("YYYY-MM-DD HH:mm")} (${start.toISOString()})`,
          `End: ${end.format("YYYY-MM-DD HH:mm")} (${end.toISOString()})`,
          this.state.reprocessKeepExisting
            ? "Existing status rows will be kept."
            : "Existing status rows in this window will be deleted and rebuilt."
        ]
      };
    }

    if (!this.state.reprocessDay) {
      return { error: "Choose a device-local day to reprocess." };
    }

    payload.day = this.state.reprocessDay;

    return {
      payload,
      preview: [
        "Mode: device-local day",
        `Status interval: ${intervalMinutes} minute${intervalMinutes === 1 ? "" : "s"}`,
        `Targets: ${selectedTargetTypes.join(", ")}`,
        `Day: ${this.state.reprocessDay}`,
        "Replay will use the device timezone for the selected day.",
        this.state.reprocessKeepExisting
          ? "Existing status rows will be kept."
          : "Existing status rows for that day will be deleted and rebuilt."
      ]
    };
  };

  reviewReprocessStatuses = () => {
    const details = this.getReprocessRequestDetails();
    if (details.error) {
      this.setState({
        reprocessError: details.error,
        reprocessConfirming: false,
        reprocessPreview: null
      });
      return;
    }

    this.setState({
      reprocessError: "",
      reprocessResult: null,
      reprocessConfirming: true,
      reprocessPreview: details.preview
    });
  };

  pollReprocessStatus = async (deviceId, jobId) => {
    const { axios, onDeviceDataRefresh } = this.props;

    try {
      const job = await getReprocessDeviceStatus(axios, deviceId, jobId);
      const nextProgress = job.progress || null;
      const nextStatus = job.status || "running";
      const nextMessage = job.message || "Replay is running.";
      const nextPercent = job.progress_percent || 0;

      if (!this._isMounted) {
        return;
      }

      if (nextStatus === "completed") {
        this.clearReprocessPollTimeout();
        this.setState({
          reprocessSubmitting: false,
          reprocessConfirming: false,
          reprocessResult: job.result || null,
          reprocessJobStatus: nextStatus,
          reprocessJobProgress: nextProgress,
          reprocessProgressPercent: 100,
          reprocessProgressMessage: nextMessage
        });
        if (onDeviceDataRefresh) {
          await onDeviceDataRefresh();
        }
        return;
      }

      if (nextStatus === "failed") {
        this.clearReprocessPollTimeout();
        this.setState({
          reprocessSubmitting: false,
          reprocessJobStatus: nextStatus,
          reprocessJobProgress: nextProgress,
          reprocessProgressPercent: nextPercent,
          reprocessProgressMessage: nextMessage,
          reprocessError: job.error || nextMessage || "Failed to reprocess statuses."
        });
        return;
      }

      this.setState({
        reprocessJobStatus: nextStatus,
        reprocessJobProgress: nextProgress,
        reprocessProgressPercent: nextPercent,
        reprocessProgressMessage: nextMessage
      });

      this.clearReprocessPollTimeout();
      this.reprocessPollTimeout = setTimeout(() => {
        this.pollReprocessStatus(deviceId, jobId);
      }, 1500);
    } catch (error) {
      if (!this._isMounted) {
        return;
      }
      this.clearReprocessPollTimeout();
      const errorMessage =
        (error.response &&
          error.response.data &&
          (error.response.data.error || error.response.data.detail)) ||
        error.message ||
        "Failed to fetch replay status.";
      this.setState({
        reprocessSubmitting: false,
        reprocessError: errorMessage
      });
    }
  };

  submitReprocessStatuses = async () => {
    const { axios, onDeviceDataRefresh } = this.props;
    const { deviceData } = this.state;
    if (!deviceData || !deviceData.ip_address) {
      return;
    }

    const details = this.getReprocessRequestDetails();
    if (details.error) {
      this.setState({
        reprocessError: details.error,
        reprocessConfirming: false,
        reprocessPreview: null
      });
      return;
    }

    this.setState({
      reprocessSubmitting: true,
      reprocessError: "",
      reprocessResult: null,
      reprocessJobStatus: "queued",
      reprocessJobProgress: null,
      reprocessProgressPercent: 0,
      reprocessProgressMessage: "Replay queued..."
    });

    try {
      const job = await startReprocessDeviceStatuses(
        axios,
        deviceData.ip_address,
        details.payload
      );

      this.setState({
        reprocessJobId: job.job_id,
        reprocessJobStatus: job.status || "queued",
        reprocessJobProgress: job.progress || null,
        reprocessProgressPercent: job.progress_percent || 0,
        reprocessProgressMessage: job.message || "Replay queued.",
        reprocessConfirming: false
      });

      this.pollReprocessStatus(deviceData.ip_address, job.job_id);
    } catch (error) {
      const errorMessage =
        (error.response &&
          error.response.data &&
          (error.response.data.error || error.response.data.detail)) ||
        error.message ||
        "Failed to reprocess statuses.";
      this.setState({
        reprocessError: errorMessage,
        reprocessSubmitting: false,
        reprocessJobStatus: "failed"
      });
    }
  };

  addNewStatusType() {
    const { deviceData } = this.state;
    const availableStatusTypes = [
      ...(deviceData.available_status_types || [])
    ];
    const nextStatusType = {
      name: "",
      target_type: "",
      update_trigger: "",
      report_period: null,
      translation_schema: {}
    };
    availableStatusTypes.push(nextStatusType);
    const l = availableStatusTypes.length - 1;

    const updatedDeviceData = {
      ...deviceData,
      available_status_types: availableStatusTypes
    };

    const updatedData = {
      ...(this.state.updatedData || {}),
      available_status_types: availableStatusTypes,
      available_device_types: deviceData.available_device_types
    };

    this.setState({
      deviceData: updatedDeviceData,
      visibleStatusType: l,
      updatedavailable_status_types: availableStatusTypes,
      updatedData: updatedData,
      editStatusTypeDetails: true
    });
  }

  resetUpdateState = data => {
    this.setState({
      deviceData: data,
      updatedData: {},
      editStatusTypeDetails: false,
      visibleStatusType: null,
      editDeviceDetails: false,
      summaryKeysAdded: [],
      summaryKeysRemoved: [],
      summaryKeyToAdd: ""
    });
  };

  updateStatusType(schemaOverride) {
    const { deviceData } = this.state;
    const updatedStatusData = [
      ...(
      this.state.updatedavailable_status_types ||
      deviceData.available_status_types || [])
    ];

    if (
      schemaOverride !== undefined &&
      this.state.visibleStatusType !== null &&
      updatedStatusData[this.state.visibleStatusType]
    ) {
      updatedStatusData[this.state.visibleStatusType] = {
        ...updatedStatusData[this.state.visibleStatusType],
        translation_schema: schemaOverride
      };
    }

    let updatedData = this.state.updatedData || {};
    updatedData.available_status_types = updatedStatusData;
    updatedData.available_device_types = deviceData.available_device_types;
    if (updatedStatusData != null) {
      updateUserDevice(this.props.axios, deviceData.ip_address, updatedData)
        .then(data => {
          this.resetUpdateState(data);
        })
        .catch(() => {
          alert("Failed to update!");
        });
    } else {
      this.setState({
        visibleStatusType: null,
        editStatusTypeDetails: false
      });
    }
  }

  renderDeviceDataTable(deviceData) {
    const { classes } = this.props;
    const settings = deviceData.other_data || {};
    const detailRows = [
      ["Name", deviceData.alias || "-"],
      ["Type", deviceData.type || "-"],
      ["IP Address", deviceData.ip_address || "-"],
      ["Installation Date", deviceData.installation_date || "-"],
      ["Location", deviceData.address || "-"],
      ["Device Data SIM Contact", deviceData.device_contact || "-"]
    ];

    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Paper className={classes.materialSectionCard} elevation={0}>
            <div
              className={classes.materialSectionHeader}
              onClick={() =>
                this.setState({ detailsExpanded: !this.state.detailsExpanded })
              }
            >
              <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                Device Details
              </Typography>
              <IconButton size="small">
                {this.state.detailsExpanded ? (
                  <VisibilityOff />
                ) : (
                  <Visibility />
                )}
              </IconButton>
            </div>
            {this.state.detailsExpanded && (
              <div className={classes.materialSectionBody}>
                {detailRows.map((row, idx) => (
                  <React.Fragment key={`detail-row-${idx}`}>
                    <div className={classes.infoRow}>
                      <div className={classes.infoLabel}>{row[0]}</div>
                      <div className={classes.infoValue}>{row[1]}</div>
                    </div>
                    {idx !== detailRows.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                <Divider />
                <div className={classes.infoRow}>
                  <div className={classes.infoLabel}>Token</div>
                  <div className={classes.infoValue}>
                    {(() => {
                      const { showToken } = this.state;
                      return (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ userSelect: "all" }}>
                            {showToken
                              ? deviceData.token
                              : "••••••••••••••••••••"}
                          </span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              this.setState({ showToken: !showToken })
                            }
                            style={{ marginLeft: 4 }}
                            title={showToken ? "Hide" : "Show"}
                          >
                            {showToken ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                          {showToken && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(deviceData.token);
                              }}
                              style={{ marginLeft: 2 }}
                              title="Copy"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                  ry="2"
                                />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            </IconButton>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </Paper>
        </GridItem>

        <GridItem xs={12} sm={12} md={12}>
          <Paper className={classes.materialSectionCard} elevation={0}>
            <div className={classes.materialSectionHeader}>
              <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                Settings
              </Typography>
            </div>
            <div className={classes.materialSectionBody}>
              <div className={classes.settingsRow}>
                <Chip
                  size="small"
                  color={settings.enableStatusCharts ? "primary" : "default"}
                  label={`Status Charts: ${
                    settings.enableStatusCharts ? "Enabled" : "Disabled"
                  }`}
                />
                <Chip
                  size="small"
                  color={
                    settings.device_load_detection_on ? "primary" : "default"
                  }
                  label={`Load Detection: ${
                    settings.device_load_detection_on ? "Enabled" : "Disabled"
                  }`}
                />
              </div>
            </div>
          </Paper>
        </GridItem>
        {deviceData.meters &&
          deviceData.meters.length > 0 && (
            <GridItem xs={12} sm={12} md={12}>
              <Paper className={classes.materialSectionCard} elevation={0}>
                <div
                  className={classes.materialSectionHeader}
                  onClick={() =>
                    this.setState({
                      metersExpanded: !this.state.metersExpanded
                    })
                  }
                >
                  <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                    Meters
                  </Typography>
                  <IconButton size="small">
                    {this.state.metersExpanded ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </div>
                {this.state.metersExpanded && (
                  <div className={classes.materialSectionBody}>
                    {deviceData.meters.map(meter => (
                      <Chip
                        key={`meter-chip-${meter.id}`}
                        className={classes.meterChip}
                        variant="outlined"
                        color="primary"
                        label={`${meter.name}: ${meter.meter_type}`}
                      />
                    ))}
                  </div>
                )}
              </Paper>
            </GridItem>
          )}
      </GridContainer>
    );
  }

  renderDeviceDataTableEditable(deviceData) {
    const { classes } = this.props;
    const meterTypeOptions = deviceData.available_meter_types
      ? deviceData.available_meter_types.map(meterType => {
          return { text: meterType, value: meterType };
        })
      : [];

    let deviceType = this.state.updatedData.type || deviceData.type;
    const updatedOtherData = this.state.updatedData.other_data || {};
    const otherData = deviceData.other_data || {};
    const enableStatusCharts =
      updatedOtherData.enableStatusCharts !== undefined
        ? updatedOtherData.enableStatusCharts
        : !!otherData.enableStatusCharts;
    const deviceLoadDetectionOn =
      updatedOtherData.device_load_detection_on !== undefined
        ? updatedOtherData.device_load_detection_on
        : !!otherData.device_load_detection_on;

    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Paper className={classes.materialSectionCard} elevation={0}>
            <div className={classes.materialSectionHeader}>
              <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                Edit Details
              </Typography>
            </div>
            <div className={classes.materialSectionBody}>
              <Typography variant="body2" className={classes.sectionDescription}>
                Update core device metadata, location details, and device-specific settings.
              </Typography>
              <GridContainer className={classes.detailsContainer}>
                <GridItem xs={12} sm={12} md={6}>
                  <div className={classes.editFieldCard}>
                    <CustomInput
                      key="dev-alias-input"
                      labelText="Device Name"
                      id="alias"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        type: "text",
                        autoComplete: "off",
                        value: this.state.updatedData.alias || deviceData.alias,
                        onChange: this.onChange("alias")
                      }}
                    />
                  </div>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <div className={classes.editFieldCard}>
                    <label className={classes.editLabel}>Device Type</label>
                    <CustomSelect
                      labelText={deviceType}
                      items={deviceTypeOptions}
                      onChange={this.onSelectionChange("type")}
                    />
                  </div>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <div className={classes.editFieldCard}>
                    <CustomInput
                      key="dev-installation-date-input"
                      labelText="Installation Date"
                      id="installation_date"
                      formControlProps={{
                        fullWidth: true
                      }}
                      labelProps={{
                        shrink: true
                      }}
                      inputProps={{
                        type: "date",
                        value:
                          this.state.updatedData.installation_date ||
                          deviceData.installation_date || "",
                        onChange: this.onChange("installation_date")
                      }}
                    />
                  </div>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <div className={classes.editFieldCard}>
                    <CustomInput
                      key="dev-address-input"
                      labelText="Device Address"
                      id="alias"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        multiline: true,
                        rows: 3,
                        value:
                          this.state.updatedData.address || deviceData.address,
                        onChange: this.onChange("address")
                      }}
                    />
                  </div>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <div className={classes.editFieldCard}>
                    <CustomInput
                      key="dev-contact-input"
                      labelText="Device Contact"
                      id="alias"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        type: "tel",
                        autoComplete: "tel",
                        value:
                          this.state.updatedData.device_contact ||
                          deviceData.device_contact,
                        onChange: this.onChange("device_contact")
                      }}
                    />
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </Paper>
        </GridItem>

        <GridItem xs={12} sm={12} md={12}>
          <Paper className={classes.materialSectionCard} elevation={0}>
            <div className={classes.materialSectionHeader}>
              <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                Settings
              </Typography>
            </div>
            <div className={classes.materialSectionBody}>
              <Typography variant="body2" className={classes.sectionDescription}>
                Toggle optional capabilities exposed by the backend for this device.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={enableStatusCharts}
                    onChange={this.onOtherDataCheckboxChange("enableStatusCharts")}
                  />
                }
                label="Enable Status Charts"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={deviceLoadDetectionOn}
                    onChange={this.onOtherDataCheckboxChange(
                      "device_load_detection_on"
                    )}
                  />
                }
                label="Device Load Detection"
              />
            </div>
          </Paper>
        </GridItem>

        <GridItem xs={12} sm={12} md={12}>
          <Paper className={classes.materialSectionCard} elevation={0}>
            <div className={classes.materialSectionHeader}>
              <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                Meters
              </Typography>
            </div>
            <div className={classes.materialSectionBody}>
              <Typography variant="body2" className={classes.sectionDescription}>
                Assign meter roles so telemetry and charts map to the correct sources.
              </Typography>
              <GridContainer className={classes.metersContainer}>
                {deviceData.meters.map(meter => {
                  return (
                    <GridItem
                      xs={12}
                      sm={12}
                      md={6}
                      key={`meter-type-${meter.id}`}
                    >
                      <div className={classes.editFieldCard}>
                        <label className={classes.editLabel}>{meter.name}</label>
                        <CustomSelect
                          labelText={meter.meter_type}
                          items={meterTypeOptions}
                          onChange={this.onMeterTypeSelectionChange(meter.id)}
                        />
                      </div>
                    </GridItem>
                  );
                })}
              </GridContainer>
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
    );
  }

  renderDevicesMap(deviceData) {
    if (
      deviceData.position.latitude == null ||
      deviceData.position.longitude == null
    ) {
      return null;
    }
    return (
      <GridContainer>
        <MapLeft
          center={[deviceData.position.latitude, deviceData.position.longitude]}
          dataPoints={[deviceData.position]}
          zoom={8}
        />
      </GridContainer>
    );
  }

  renderDeviceDataInputTableEditable(deviceData) {
    const { classes } = this.props;

    return (
      <GridContainer className={classes.inputSectionCard}>
        <GridItem xs={12} sm={12} md={12}>
          <Paper className={classes.materialSectionCard} elevation={0}>
            <div className={classes.materialSectionHeader}>
              <Typography
                variant="subtitle1"
                className={classes.materialSectionTitle}
              >
                Summary &amp; Limits
              </Typography>
            </div>
            <div className={classes.materialSectionBody}>
              <Typography variant="body2" className={classes.sectionDescription}>
                Set billing limits and energy targets used by summary widgets on the dashboard.
              </Typography>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginBottom: "10px" }}>
                <Button color="primary" size="sm" onClick={this.addAllDefaults}>
                  Add All Defaults
                </Button>
                <Button color="info" size="sm" onClick={this.addReportDefaults}>
                  Add Report Defaults
                </Button>
              </div>
              {(() => {
                const existingKeys = SUMMARY_PROPERTY_HELPER_KEYS.filter(
                  k => deviceData.properties && deviceData.properties[k] != null && deviceData.properties[k] !== ""
                );
                const activeKeys = [
                  ...new Set([...existingKeys, ...this.state.summaryKeysAdded])
                ].filter(k => !this.state.summaryKeysRemoved.includes(k));
                const remainingKeys = SUMMARY_PROPERTY_HELPER_KEYS.filter(
                  k => !activeKeys.includes(k)
                );
                return (
                  <React.Fragment>
                    <GridContainer>
                      {activeKeys.map(key => (
                        <GridItem xs={12} sm={12} md={6} key={`summary-option-${key}`}>
                          {(() => {
                            const meta = getSummaryPropertyMeta(key);
                            return (
                          <div style={{ position: "relative" }}>
                            <div className={classes.stateFieldCard}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                                <Typography variant="subtitle2" style={{ color: "#1e293b" }}>
                                  {meta.label}
                                </Typography>
                                <Tooltip
                                  title={`${meta.description || "No description provided."} ${meta.group ? `Used in: ${meta.group}.` : ""}${meta.suggestedDefault ? ` Suggested default: ${meta.suggestedDefault}.` : ""}`.trim()}
                                  placement="top"
                                >
                                  <InfoOutlinedIcon style={{ fontSize: "16px", color: "#64748b" }} />
                                </Tooltip>
                              </div>
                              <CustomInput
                                labelText="Value"
                                formControlProps={{ fullWidth: true }}
                                inputProps={{
                                  ...(SUMMARY_TEXT_KEYS.has(key)
                                    ? { type: "text", autoComplete: "off" }
                                    : { type: "number", inputMode: "decimal", step: "any" }),
                                  value:
                                    (this.state.updatedData.properties &&
                                      this.state.updatedData.properties[key] != null
                                      ? this.state.updatedData.properties[key]
                                      : (deviceData.properties && deviceData.properties[key]) || ""),
                                  onChange: this.onStateInputChange(key)
                                }}
                              />
                              <Typography variant="caption" className={classes.topBarSubtitle}>
                                {meta.group}
                                {meta.description ? ` - ${meta.description}` : ""}
                                {meta.suggestedDefault ? ` Suggested default: ${meta.suggestedDefault}.` : ""}
                              </Typography>
                            </div>
                            <IconButton
                              size="small"
                              onClick={() => this.removeSummaryKey(key)}
                              style={{ position: "absolute", top: 4, right: 4 }}
                              title="Remove"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                            );
                          })()}
                        </GridItem>
                      ))}
                    </GridContainer>
                    {remainingKeys.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px" }}>
                        <select
                          value={this.state.summaryKeyToAdd}
                          onChange={e => this.setState({ summaryKeyToAdd: e.target.value })}
                          style={{
                            flex: 1,
                            padding: "8px 10px",
                            borderRadius: "8px",
                            border: "1px solid #e3eef8",
                            fontSize: "14px",
                            color: "#263238",
                            background: "#fff"
                          }}
                        >
                          <option value="">Select a setting to add…</option>
                          {remainingKeys.map(k => (
                            <option key={k} value={k}>
                              {`${getSummaryPropertyMeta(k).group} - ${getSummaryPropertyMeta(k).label}`}
                            </option>
                          ))}
                        </select>
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!this.state.summaryKeyToAdd}
                          onClick={() => this.addSummaryKey(this.state.summaryKeyToAdd)}
                        >
                          Add
                        </Button>
                      </div>
                    )}
                  </React.Fragment>
                );
              })()}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
    );
  }

  renderDocuments(deviceData, sectionId) {
    const { classes } = this.props;
    const documents = deviceData.documents || [];
    // State for modal
    const { documentModalOpen, documentModalIndex } = this.state;
    const currentDoc = documents[documentModalIndex] || {};

    return (
      <GridItem id={sectionId} xs={12} sm={12} md={12}>
        <Paper className={classes.deviceDetailsSectionContainer} elevation={0}>
          <div className={classes.deviceTypeContainer}>
            <div className={classes.sectionTitleRow}>
              <Typography
                variant="subtitle1"
                className={classes.materialSectionTitle}
              >
                Documents
              </Typography>
              <Typography variant="body2" className={classes.topBarSubtitle}>
                Upload manuals, drawings, and reference files for this device.
              </Typography>
            </div>
            <IconButton
              color="primary"
              onClick={() =>
                this.setState({
                  addingDocument: true,
                  newDoc: { doc_name: "", doc_type: "", file: null }
                })
              }
              style={{ marginLeft: 8 }}
            >
              <AddIcon />
            </IconButton>
          </div>
          <div className={classes.materialSectionBody}>
            {this.state.addingDocument && (
              <div className={classes.inlineFormCard}>
                <GridContainer>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText="Document Name"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        type: "text",
                        value:
                          (this.state.newDoc && this.state.newDoc.doc_name) ||
                          "",
                        onChange: e =>
                          this.setState({
                            newDoc: {
                              ...this.state.newDoc,
                              doc_name: e.target.value
                            }
                          })
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={6} md={3}>
                    <CustomInput
                      labelText="Document Type"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        type: "text",
                        value:
                          (this.state.newDoc && this.state.newDoc.doc_type) ||
                          "",
                        onChange: e =>
                          this.setState({
                            newDoc: {
                              ...this.state.newDoc,
                              doc_type: e.target.value
                            }
                          })
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={3}>
                    <CustomInput
                      labelText="File"
                      labelProps={{ shrink: true }}
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        type: "file",
                        accept: "*",
                        onChange: e =>
                          this.setState({
                            newDoc: {
                              ...this.state.newDoc,
                              file: e.target.files[0]
                            }
                          })
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <div className={classes.deviceInputActions}>
                      <Button
                        color="info"
                        size="sm"
                        onClick={async () => {
                          const { newDoc } = this.state;
                          const { deviceData, axios } = this.props;
                          if (
                            !newDoc.doc_name ||
                            !newDoc.doc_type ||
                            !newDoc.file
                          ) {
                            alert("Please fill all fields and select a file.");
                            return;
                          }
                          const docData = {
                            device_ip_address: deviceData.ip_address,
                            device_id: deviceData.id,
                            doc_name: newDoc.doc_name,
                            doc_type: newDoc.doc_type
                          };
                          try {
                            await uploadDeviceDocs(axios, docData, newDoc.file);
                            this.setState(
                              {
                                addingDocument: false,
                                newDoc: null,
                                documentsLoaded: false
                              },
                              () => this.fetchDeviceDocsIfNeeded()
                            );
                          } catch (err) {
                            alert("Failed to upload document");
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        color="default"
                        size="sm"
                        onClick={() =>
                          this.setState({
                            addingDocument: false,
                            newDoc: null
                          })
                        }
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridItem>
                </GridContainer>
              </div>
            )}
          <Table
            className={classes.detailsTable}
            tableHeaderColor="primary"
            tableHead={["Name", "Type", "Created At", "Actions"]}
            tableData={[
              ...documents.map((doc, idx) => [
                <span
                  key={doc.id}
                  className={classes.documentTableLink}
                  onClick={() =>
                    this.setState({
                      documentModalOpen: true,
                      documentModalIndex: idx
                    })
                  }
                >
                  {doc.doc_name}
                </span>,
                doc.doc_type,
                doc.created_at,
                // Actions column: show delete if user has permission
                this.props.user &&
                (this.props.user.isAdmin || this.props.user.isSuperuser) ? (
                  <IconButton
                    color="secondary"
                    size="small"
                    title="Delete"
                    onClick={async () => {
                      if (
                        window.confirm(
                          `Are you sure you want to delete "${doc.doc_name}"?`
                        )
                      ) {
                        try {
                          await deleteDocument(this.props.axios, doc.id);
                          this.setState({ documentsLoaded: false }, () =>
                            this.fetchDeviceDocsIfNeeded()
                          );
                        } catch (err) {
                          alert("Failed to delete document");
                        }
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : null
              ])
            ]}
          />
          </div>
          <Modal
            open={!!documentModalOpen}
            onClose={() => this.setState({ documentModalOpen: false })}
            aria-labelledby="Document Preview"
            aria-describedby="Preview of the selected document"
          >
            <Box
              className={classes.alarmModalBox}
              sx={{ position: "relative" }}
            >
              <div className={classes.modalHeader}>
                <div className={classes.modalTitleBlock}>
                  <Typography variant="h6">{currentDoc.doc_name || "Document"}</Typography>
                  <Typography variant="body2" className={classes.topBarSubtitle}>
                    {currentDoc.doc_type || "No type specified"}
                  </Typography>
                  {currentDoc.description && (
                    <Typography variant="body2">{currentDoc.description}</Typography>
                  )}
                </div>
                <IconButton
                  onClick={() => this.setState({ documentModalOpen: false })}
                  className={classes.modalNavButton}
                >
                  ×
                </IconButton>
              </div>
              <div className={classes.topBarContent}>
                <IconButton
                  className={classes.modalNavButton}
                  onClick={e => {
                    e.stopPropagation();
                    this.setState({
                      documentModalIndex:
                        (documentModalIndex - 1 + documents.length) %
                        documents.length
                    });
                  }}
                  disabled={documents.length <= 1}
                >
                  {"<"}
                </IconButton>
                <Chip
                  label={`${documentModalIndex + 1} / ${documents.length || 1}`}
                  color="primary"
                  variant="outlined"
                />
                <IconButton
                  className={classes.modalNavButton}
                  onClick={e => {
                    e.stopPropagation();
                    this.setState({
                      documentModalIndex:
                        (documentModalIndex + 1) % documents.length
                    });
                  }}
                  disabled={documents.length <= 1}
                >
                  {">"}
                </IconButton>
              </div>
              <div className={classes.modalPreview}>
                {currentDoc.document ? (
                  currentDoc.document.toLowerCase().includes(".png") ||
                  currentDoc.document.toLowerCase().includes(".jpg") ||
                  currentDoc.document.toLowerCase().includes(".jpeg") ? (
                    <img
                      src={currentDoc.document}
                      alt={currentDoc.doc_name}
                      className={classes.modalPreviewImage}
                    />
                  ) : currentDoc.doc_type &&
                  currentDoc.document.toLowerCase().includes(".pdf") ? (
                    <object
                      data={currentDoc.document}
                      type="application/pdf"
                      width="100%"
                      height="400"
                    >
                      <a
                        href={currentDoc.document}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open PDF
                      </a>
                    </object>
                  ) : (
                    <a
                      href={currentDoc.document}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Document
                    </a>
                  )
                ) : (
                  <span>No preview available</span>
                )}
              </div>
            </Box>
          </Modal>
        </Paper>
      </GridItem>
    );
  }

  renderStatusTypes(deviceData, sectionId) {
    const { user, classes } = this.props;
    const statusTypes = deviceData.available_status_types || [];
    return (
      <GridItem id={sectionId} xs={12} sm={12} md={12}>
        <Paper className={classes.deviceDetailsSectionContainer} elevation={0}>
          <div className={classes.deviceTypeContainer}>
            <div className={classes.sectionTitleRow}>
              <Typography
                variant="subtitle1"
                className={classes.materialSectionTitle}
              >
                Status Types
              </Typography>
              <Typography variant="body2" className={classes.topBarSubtitle}>
                Manage schema mappings used to translate raw device status payloads.
              </Typography>
            </div>
            <IconButton
              color="primary"
              onClick={() => this.addNewStatusType()}
              disabled={!(user.isAdmin === true || user.isSuperuser === true)}
              style={{ marginLeft: 8 }}
            >
              <AddIcon />
            </IconButton>
          </div>
          <div className={classes.materialSectionBody}>
          <div className={classes.helperCard}>
            <Typography variant="body2" className={classes.helperTitle}>
              Suggested Mapping Targets
            </Typography>
            <Typography variant="body2" className={classes.helperSubtitle}>
              Add these keys in status type translation mappings so LCD, charts,
              and summary widgets render complete data.
            </Typography>

            <Typography variant="caption" className={classes.helperGroupTitle}>
              LCD Status Keys
            </Typography>
            <div className={classes.helperChipRow}>
              {LCD_STATUS_HELPER_KEYS.map(key => (
                <Chip
                  key={`lcd-helper-${key}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={key}
                  className={classes.helperChip}
                />
              ))}
            </div>

            <Typography variant="caption" className={classes.helperGroupTitle}>
              Chart Status Keys
            </Typography>
            <div className={classes.helperChipRow}>
              {CHART_STATUS_HELPER_KEYS.map(key => (
                <Chip
                  key={`chart-helper-${key}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={key}
                  className={classes.helperChip}
                />
              ))}
            </div>

            <Typography variant="caption" className={classes.helperGroupTitle}>
              Summary Property Keys
            </Typography>
            <div className={classes.helperChipRow}>
              {SUMMARY_PROPERTY_HELPER_KEYS.map(key => (
                <Chip
                  key={`summary-helper-${key}`}
                  size="small"
                  variant="outlined"
                  color="default"
                  label={key}
                  className={classes.helperChip}
                />
              ))}
            </div>
          </div>
          <Table
            className={classes.detailsTable}
            tableHeaderColor="primary"
            tableHead={["Name", "Target Type", "Created At", "Actions"]}
            tableData={statusTypes.map((statusType, idx) => [
              <span
                key={statusType.name || idx}
                className={classes.documentTableLink}
                onClick={() => this.setState({ visibleStatusType: idx })}
              >
                {statusType.name}
              </span>,
              statusType.target_type,
              statusType.created_at || "",
              <div key={`status-type-actions-${idx}`}>
                <IconButton
                  onClick={() => this.setState({ visibleStatusType: idx })}
                  size="small"
                  title="View/Edit"
                >
                  <Visibility />
                </IconButton>
                {(user.isAdmin || user.isSuperuser) && (
                  <IconButton
                    disabled={
                      !(user.isAdmin === true || user.isSuperuser === true)
                    }
                    color="secondary"
                    size="small"
                    title="Delete"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Are you sure you want to delete "${
                            statusType.name
                          }"?`
                        )
                      ) {
                        this.removeStatusType(idx);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            ])}
          />
          </div>
          {/* Show modal for editing/viewing status type */}
          {statusTypes.map((statusType, idx) =>
            this.state.visibleStatusType === idx ? (
              <StatusTypeEditor
                key={`status-type-editor-${idx}`}
                open={this.state.visibleStatusType === idx}
                statusType={statusType}
                axios={this.props.axios}
                deviceId={deviceData.ip_address}
                onClose={() =>
                  this.setState({
                    visibleStatusType: null,
                    editStatusTypeDetails: false
                  })
                }
                onUpdate={schema => this.updateStatusType(schema)}
                onDelete={() => this.removeStatusType(idx)}
                onChange={this.onStatusTypeChange(idx)}
                onSchemaChange={this.onStatusTypeSchemaChange(idx)}
                canEdit={
                  this.props.user.isAdmin === true ||
                  this.props.user.isSuperuser === true
                }
                enableInteractiveMode={true}
                rawData={
                  (deviceData &&
                    deviceData.latest_data &&
                    deviceData.latest_data.raw_data_last_5) ||
                  []
                }
              />
            ) : null
          )}
        </Paper>
      </GridItem>
    );
  }

  onStatusTypeChange = idx => (fieldName, value) => {
    const { deviceData } = this.state;
    let availableStatusTypes = [
      ...(deviceData.available_status_types || [])
    ];
    if (availableStatusTypes[idx]) {
      const nextStatusType = {
        ...availableStatusTypes[idx],
        [fieldName]: value
      };
      if (fieldName === "target_type" && value !== "report") {
        nextStatusType.report_period = null;
      }
      availableStatusTypes[idx] = {
        ...nextStatusType
      };
      let updatedData = this.state.updatedData || {};
      updatedData.available_status_types = availableStatusTypes;
      this.setState({
        deviceData: {
          ...deviceData,
          available_status_types: availableStatusTypes
        },
        updatedavailable_status_types: availableStatusTypes,
        updatedData: updatedData
      });
    }
  };

  onStatusTypeSchemaChange = idx => schema => {
    this.onStatusTypeChange(idx)("translation_schema", schema);
  };

  removeStatusType = idx => {
    const { deviceData } = this.state;
    let availableStatusTypes = [...(deviceData.available_status_types || [])];

    // Set the status type as inactive instead of removing it
    if (availableStatusTypes[idx]) {
      availableStatusTypes[idx] = {
        ...availableStatusTypes[idx],
        active: false
      };
    }

    // Prepare updated data for API call - include the full status type object with id
    let updatedData = {
      available_status_types: availableStatusTypes,
      available_device_types: deviceData.available_device_types
    };

    // Call API to update device with inactive status type
    updateUserDevice(this.props.axios, deviceData.ip_address, updatedData)
      .then(data => {
        this.resetUpdateState(data);
      })
      .catch(() => {
        alert("Failed to delete status type!");
      });
  };

  render() {
    // eslint-disable-next-line no-unused-vars
    const { classes, user } = this.props;
    const { deviceData } = this.state;
    const hasPendingChanges =
      this.state.updatedData && Object.keys(this.state.updatedData).length > 0;

    if (deviceData === undefined) return null;
    if (deviceData.available_device_types != null) {
      deviceTypeOptions = deviceData.available_device_types;
    }

    return (
      <div className={classes.root}>
        {this.renderDevicesMap(deviceData)}
        <Paper className={classes.topBar} elevation={0}>
          <div className={classes.topBarContent}>
            <div className={classes.topBarTitleBlock}>
              <Typography variant="h6" className={classes.topBarTitle}>
                Device Configuration
              </Typography>
              <Typography variant="body2" className={classes.topBarSubtitle}>
                Review metadata, configure device mappings, and manage supporting schemas and files.
              </Typography>
            </div>
            {(user.isAdmin || user.isSuperuser) && (
              <div className={classes.topBarActions}>
                <Button
                  size="sm"
                  color="info"
                  onClick={this.openReprocessModal}
                  className={classes.adminActionButton}
                >
                  Reprocess Statuses
                </Button>
                <Chip
                  size="small"
                  color={this.state.editDeviceDetails ? "primary" : "default"}
                  label={this.state.editDeviceDetails ? "Edit Mode On" : "Read Only"}
                />
                <div className={classes.statusSummaryControls}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={this.state.editDeviceDetails === true}
                        onChange={() => {
                          this.setState({
                            editDeviceDetails: !this.state.editDeviceDetails
                          });
                        }}
                      />
                    }
                    label="Edit details"
                  />
                </div>
              </div>
            )}
          </div>
        </Paper>
        {this.state.editDeviceDetails === true && (
          <div className={`${classes.globalEditActions} ${classes.globalEditActionsDesktop}`}>
            <Typography variant="body2" className={classes.globalEditActionsText}>
              Editing configuration sections. Save once after reviewing all changes.
            </Typography>
            <div className={classes.globalEditActionsButtons}>
              <Button
                color="info"
                disabled={!hasPendingChanges}
                onClick={this.updateProfile(deviceData.ip_address)}
              >
                Update All Changes
              </Button>
              <Button
                color="default"
                disabled={!hasPendingChanges}
                onClick={() => {
                  this.setState({
                    editDeviceDetails: false,
                    updatedData: {}
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {this.state.editDeviceDetails === true && (
          <div className={`${classes.globalEditActions} ${classes.globalEditActionsMobile}`}>
            <Typography variant="body2" className={classes.globalEditActionsText}>
              Save all edits
            </Typography>
            <div className={classes.globalEditActionsButtons}>
              <Button
                color="info"
                disabled={!hasPendingChanges}
                onClick={this.updateProfile(deviceData.ip_address)}
              >
                Update
              </Button>
              <Button
                color="default"
                disabled={!hasPendingChanges}
                onClick={() => {
                  this.setState({
                    editDeviceDetails: false,
                    updatedData: {}
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        <GridContainer>
          {!(this.state.editDeviceDetails === true) ? (
            this.renderDeviceDataTable(deviceData)
          ) : (
            <GridItem xs={12} sm={12} md={12}>
              <Paper className={classes.editWorkspaceShell} elevation={0}>
                <div className={classes.editWorkspaceHeader}>
                  <div>
                    <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                      Editing Workspace
                    </Typography>
                    <Typography variant="body2" className={classes.topBarSubtitle}>
                      Configure core identity, meter mapping, and report properties in one continuous flow.
                    </Typography>
                  </div>
                  <div className={classes.editWorkspaceNav}>
                    <Chip className={classes.editWorkspaceNavChip} label="Core" clickable variant="outlined" onClick={() => this.scrollToSection("device-section-core")} />
                    <Chip className={classes.editWorkspaceNavChip} label="Report Inputs" clickable variant="outlined" onClick={() => this.scrollToSection("device-section-report")} />
                    <Chip className={classes.editWorkspaceNavChip} label="Documents" clickable variant="outlined" onClick={() => this.scrollToSection("device-section-documents")} />
                    <Chip className={classes.editWorkspaceNavChip} label="Status Types" clickable variant="outlined" onClick={() => this.scrollToSection("device-section-status")} />
                  </div>
                </div>

                <div id="device-section-core" className={classes.editWorkspaceAnchor}>
                  {this.renderDeviceDataTableEditable(deviceData)}
                </div>

                <div id="device-section-report" className={classes.editWorkspaceAnchor}>
                  {this.renderDeviceDataInputTableEditable(deviceData)}
                </div>
              </Paper>
            </GridItem>
          )}
          {this.renderDocuments(deviceData, "device-section-documents")}
          {this.renderStatusTypes(deviceData, "device-section-status")}
        </GridContainer>
        <Modal
          open={this.state.reprocessModalOpen === true}
          onClose={this.closeReprocessModal}
        >
          <Paper className={classes.alarmModalBox} elevation={0}>
            <div className={classes.materialSectionHeader}>
              <div>
                <Typography variant="subtitle1" className={classes.materialSectionTitle}>
                  Reprocess Device Statuses
                </Typography>
                <Typography variant="body2" className={classes.topBarSubtitle}>
                  Rebuild calculated statuses from stored raw data for this device.
                </Typography>
              </div>
            </div>
            <div className={classes.materialSectionBody}>
              <div className={classes.reprocessSummaryCard}>
                <Typography variant="caption" className={classes.helperGroupTitle}>
                  Scope
                </Typography>
                <div className={classes.settingsRow}>
                  <Chip
                    clickable
                    color={this.state.reprocessMode === "day" ? "primary" : "default"}
                    variant={this.state.reprocessMode === "day" ? "default" : "outlined"}
                    label="Device-local day"
                    onClick={() => this.setState({ reprocessMode: "day", reprocessError: "" })}
                  />
                  <Chip
                    clickable
                    color={this.state.reprocessMode === "range" ? "primary" : "default"}
                    variant={this.state.reprocessMode === "range" ? "default" : "outlined"}
                    label="Exact time range"
                    onClick={() => this.setState({ reprocessMode: "range", reprocessError: "" })}
                  />
                </div>

                <Typography variant="caption" className={classes.helperGroupTitle}>
                  Targets to Regenerate
                </Typography>
                <div className={classes.settingsRow}>
                  {REPROCESS_TARGET_TYPE_OPTIONS.map(option => {
                    const isSelected = (this.state.reprocessTargetTypes || []).includes(option.value);
                    return (
                      <Chip
                        key={`reprocess-target-${option.value}`}
                        clickable
                        color={isSelected ? "primary" : "default"}
                        variant={isSelected ? "default" : "outlined"}
                        label={option.label}
                        onClick={() => this.toggleReprocessTargetType(option.value)}
                      />
                    );
                  })}
                </div>

                <div className={classes.reprocessFieldsRow}>
                  <div className={classes.reprocessField}>
                    <CustomInput
                      labelText="Status interval (minutes)"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        type: "number",
                        min: 2,
                        max: 60,
                        value: this.state.reprocessStatusIntervalMinutes || 10,
                        onChange: e =>
                          this.setState({
                            reprocessStatusIntervalMinutes: e.target.value,
                            reprocessError: "",
                            reprocessConfirming: false,
                            reprocessPreview: null,
                            reprocessResult: null
                          })
                      }}
                    />
                  </div>
                  {this.state.reprocessMode === "day" ? (
                    <div className={classes.reprocessField}>
                      <CustomInput
                        labelText="Day"
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          type: "date",
                          value: this.state.reprocessDay || "",
                          onChange: e =>
                            this.setState({
                              reprocessDay: e.target.value,
                              reprocessError: "",
                              reprocessConfirming: false,
                              reprocessPreview: null,
                              reprocessResult: null
                            })
                        }}
                      />
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className={classes.reprocessField}>
                        <CustomInput
                          labelText="Start"
                          formControlProps={{ fullWidth: true }}
                          inputProps={{
                            type: "datetime-local",
                            value: this.state.reprocessStart || "",
                            onChange: e =>
                              this.setState({
                                reprocessStart: e.target.value,
                                reprocessError: "",
                                reprocessConfirming: false,
                                reprocessPreview: null,
                                reprocessResult: null
                              })
                          }}
                        />
                      </div>
                      <div className={classes.reprocessField}>
                        <CustomInput
                          labelText="End"
                          formControlProps={{ fullWidth: true }}
                          inputProps={{
                            type: "datetime-local",
                            value: this.state.reprocessEnd || "",
                            onChange: e =>
                              this.setState({
                                reprocessEnd: e.target.value,
                                reprocessError: "",
                                reprocessConfirming: false,
                                reprocessPreview: null,
                                reprocessResult: null
                              })
                          }}
                        />
                      </div>
                    </React.Fragment>
                  )}
                </div>

                <div className={classes.reprocessToggleRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={this.state.reprocessKeepExisting === true}
                        onChange={e =>
                          this.setState({
                            reprocessKeepExisting: e.target.checked,
                            reprocessError: "",
                            reprocessConfirming: false,
                            reprocessPreview: null,
                            reprocessResult: null
                          })
                        }
                      />
                    }
                    label="Keep existing status rows"
                  />
                </div>

                {this.state.reprocessPreview ? (
                  <div className={classes.reprocessConfirmationBox}>
                    <Typography variant="caption" className={classes.helperGroupTitle}>
                      Confirmation
                    </Typography>
                    {this.state.reprocessPreview.map((line, idx) => (
                      <Typography
                        key={`reprocess-preview-${idx}`}
                        variant="body2"
                        className={classes.reprocessConfirmationLine}
                      >
                        {line}
                      </Typography>
                    ))}
                  </div>
                ) : null}

                {this.state.reprocessSubmitting ? (
                  <div className={classes.reprocessConfirmationBox}>
                    <Typography variant="caption" className={classes.helperGroupTitle}>
                      Replay Progress
                    </Typography>
                    <Typography variant="body2" className={classes.reprocessConfirmationLine}>
                      {this.state.reprocessProgressMessage || "Replay is running..."}
                    </Typography>
                    <div className={classes.reprocessStatsRow}>
                      <Chip
                        size="small"
                        label={`Status: ${this.state.reprocessJobStatus || "running"}`}
                      />
                      <Chip
                        size="small"
                        label={`Progress: ${this.state.reprocessProgressPercent || 0}%`}
                      />
                      <Chip
                        size="small"
                        label={`Visited raws: ${((this.state.reprocessJobProgress || {}).processed_raw_count) || 0}`}
                      />
                      <Chip
                        size="small"
                        label={`Total raws: ${((this.state.reprocessJobProgress || {}).total_raw_count) || 0}`}
                      />
                    </div>
                  </div>
                ) : null}

                {this.state.reprocessError ? (
                  <Typography variant="body2" className={classes.reprocessError}>
                    {this.state.reprocessError}
                  </Typography>
                ) : null}

                {this.state.reprocessResult ? (
                  <div className={classes.reprocessResult}>
                    <Typography variant="body2">
                      {this.state.reprocessResult.message}
                    </Typography>
                    <div className={classes.reprocessStatsRow}>
                      <Chip size="small" label={`Visited raws: ${this.state.reprocessResult.processed_raw_count || 0}`} />
                      <Chip size="small" label={`Replayed in window: ${this.state.reprocessResult.replayed_raw_count || 0}`} />
                      <Chip size="small" label={`Deleted statuses: ${this.state.reprocessResult.deleted_status_count || 0}`} />
                      <Chip size="small" label={`Skipped status raws: ${this.state.reprocessResult.skipped_status_raw_count || 0}`} />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className={classes.deviceInputActions}>
                <Button
                  color="default"
                  onClick={this.closeReprocessModal}
                  disabled={this.state.reprocessSubmitting === true}
                >
                  Close
                </Button>
                <Button
                  color="info"
                  onClick={
                    this.state.reprocessConfirming
                      ? this.submitReprocessStatuses
                      : this.reviewReprocessStatuses
                  }
                  disabled={this.state.reprocessSubmitting === true}
                >
                  {this.state.reprocessSubmitting
                    ? this.state.reprocessJobStatus === "queued"
                      ? "Queued..."
                      : "Running..."
                    : this.state.reprocessConfirming
                    ? "Confirm and Run"
                    : "Review Reprocess"}
                </Button>
              </div>
            </div>
          </Paper>
        </Modal>
      </div>
    );
  }
}

DeviceDetailsTable.propTypes = {
  classes: PropTypes.object.isRequired,
  onDeviceDataRefresh: PropTypes.func
};

export default withStyles(styles)(DeviceDetailsTable);
